import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Checkbox, FormControlLabel, FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './returnReasonsApi';
import type { ReturnReason, Location } from './returnReasonsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    returnReasonId: number | null;
}

export function ReturnReasonView({ returnReasonId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<ReturnReason | null>(null);
    const [locations, setLocations] = useState<Location[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<ReturnReason | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const locs = await api.getLocations();
            setLocations([{ id: 0, locationName: '---' }, ...locs]);
            if (returnReasonId === null) {
                setItem({ id: 0, code: '', name: '', adjustStock: false, stockLocationId: null });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getReturnReason(returnReasonId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [returnReasonId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isCodeValid = (): boolean => !!item.code && item.code.length <= 20;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createReturnReason(item) : await api.updateReturnReason(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('returnreason.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deleteReturnReason(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof ReturnReason>(field: K, value: ReturnReason[K]) => {
        setItem({ ...item, [field]: value });
    };

    const handleAdjustStockChange = (checked: boolean) => {
        const next = { ...item, adjustStock: checked };
        if (!checked) next.stockLocationId = null;
        setItem(next);
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.code} ${item.name}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={isCodeValid()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('returnreason.code')}>
                        {!isNew && !isEditing ? (
                            <Typography sx={sx14}>{item.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.code}
                                onChange={e => update('code', e.target.value.toUpperCase())}
                                error={!isCodeValid()} />
                        )}
                    </FormRow>

                    <FormRow label={t('returnreason.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.name} onChange={e => update('name', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('returnreason.adjustStock')}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={item.adjustStock}
                                    disabled={!isEditing}
                                    onChange={e => handleAdjustStockChange(e.target.checked)}
                                />
                            }
                            label=""
                        />
                    </FormRow>

                    {item.adjustStock && (
                        <FormRow label={t('returnreason.location')}>
                            {!isEditing ? (
                                <Typography sx={sx14}>
                                    {locations.find(l => l.id === item.stockLocationId)?.locationName ?? ''}
                                </Typography>
                            ) : (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={item.stockLocationId ?? 0}
                                        onChange={e => update('stockLocationId', e.target.value === 0 ? null : Number(e.target.value))}
                                    >
                                        {locations.map(l => (
                                            <MenuItem key={l.id} value={l.id}>{l.locationName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>
                    )}
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
